import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Camera, ClipboardEdit, FileText, Loader2, Plus, ShieldAlert, Siren } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmergencyNotice } from "@/components/incidents/EmergencyNotice";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import {
  ACTION_STATUS_COLORS, ACTION_STATUS_LABELS, EVIDENCE_TYPES, INCIDENT_CATEGORY_LABELS,
  INCIDENT_STATUS_COLORS, INCIDENT_STATUS_LABELS, INCIDENT_SEVERITIES, SEVERITY_COLORS, TRAINING_DECISIONS,
} from "@/config/incidentCatalog";

export default function IncidentDetailPage() {
  const { incidentId } = useParams();
  const { hasPermission } = useAuth();
  const canReview = hasPermission("incidents:review");
  const [inc, setInc] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [actionForm, setActionForm] = useState({ action: "", assigned_to: "", due_date: "", notes: "" });
  const [amendForm, setAmendForm] = useState({ reason: "", note: "" });
  const [reviewForm, setReviewForm] = useState({ findings: "", further_action: "", closure_decision: "", new_severity: "", comments: "", risk_review: false, journey_plan_review: false, training_plan_review: false, safeguarding_referral: false, parent_notification: false, school_notification: false, la_notification: false });
  const [decisionForm, setDecisionForm] = useState({ decision: "", comments: "" });
  const [staff, setStaff] = useState([]);
  const [evPurpose, setEvPurpose] = useState("");
  const [evType, setEvType] = useState("photograph");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/incidents/${incidentId}`);
      setInc(data);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [incidentId]);

  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaff(r.data.items || [])).catch(() => {});
  }, [load]);

  const act = async (fn, success) => {
    setSaving(true);
    try {
      await fn();
      toast.success(success);
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "incident_evidence");
      fd.append("child_id", inc.child_id);
      fd.append("description", evPurpose || "Incident evidence");
      const { data: doc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/incidents/${incidentId}/evidence`, { document_id: doc.id, evidence_type: evType, description: evPurpose });
      toast.success("Evidence attached (stored privately)");
      setEvPurpose("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (denied) {
    return (
      <div data-testid="incident-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-sm text-slate-600 max-w-md">This incident does not exist or you are not authorised. This attempt has been recorded.</p>
      </div>
    );
  }
  if (!inc) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading incident…</div>;
  }

  const isDraft = inc.status === "draft";
  const yn = (v) => (v ? "Yes" : "No");

  return (
    <div className="space-y-4 max-w-3xl" data-testid="incident-page">
      <Link to="/incidents" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="incident-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to incidents
      </Link>
      <EmergencyNotice />

      <div className="rounded-lg border border-border bg-white p-5" data-testid="incident-header">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="incident-number">{inc.incident_number}</h1>
          {inc.severity === "critical" && <Badge className={SEVERITY_COLORS.critical} data-testid="incident-critical-badge">CRITICAL</Badge>}
          <Badge className={SEVERITY_COLORS[inc.severity]}>{(inc.severity || "").toUpperCase()}</Badge>
          <Badge className={INCIDENT_STATUS_COLORS[inc.status]} data-testid="incident-status">{INCIDENT_STATUS_LABELS[inc.status]}</Badge>
          {inc.is_near_miss && <Badge className="bg-indigo-50 text-indigo-700 border-transparent">Near miss</Badge>}
          {!inc.detail_complete && <Badge className="bg-amber-100 text-amber-800 border-transparent" data-testid="incident-followup-badge">FOLLOW-UP INFORMATION REQUIRED</Badge>}
          {inc.safeguarding_escalation && <Badge className="bg-red-50 text-red-700 border-transparent">Safeguarding linked</Badge>}
        </div>
        <p className="mt-1 text-sm text-slate-600">
          <Link to={`/children/${inc.child_id}`} className="text-primary hover:underline">{inc.child?.child_name}</Link> · {inc.child?.case_id} · Reported by {inc.reporter_name} · {(inc.submitted_at || "").slice(0, 10) || "Not submitted"}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {isDraft && (
            <>
              <Button variant="outline" className="h-11" disabled data-testid="incident-edit-hint">Draft — edit from incidents list</Button>
              <Button className="h-11 bg-red-600 text-white" data-testid="incident-submit-button"
                onClick={() => act(() => api.post(`/incidents/${inc.id}/submit`), "Incident submitted")}>Submit incident</Button>
            </>
          )}
          {!isDraft && (
            <>
              <Button variant="outline" className="h-11" data-testid="incident-amend-button" onClick={() => setDialog("amend")}>
                <ClipboardEdit className="h-4 w-4 mr-1" /> Add amendment
              </Button>
              <a href={`${API_URL}/incidents/${inc.id}/pdf`} target="_blank" rel="noreferrer" data-testid="incident-pdf-button"
                className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover">
                <FileText className="h-4 w-4" /> Incident report PDF
              </a>
              {!inc.safeguarding_escalation && (
                <Link to={`/safeguarding/new?incident=${inc.id}&child=${inc.child_id}`} data-testid="incident-escalate-button"
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-red-300 px-4 text-sm font-semibold text-red-700 hover:bg-red-50">
                  <Siren className="h-4 w-4" /> Raise safeguarding concern
                </Link>
              )}
              {canReview && (
                <>
                  <Button variant="outline" className="h-11" data-testid="incident-review-button" onClick={() => setDialog("review")}>Manager review</Button>
                  <Button variant="outline" className="h-11" data-testid="incident-decision-button" onClick={() => setDialog("decision")}>Training decision</Button>
                  <Button variant="outline" className="h-11" data-testid="incident-action-add" onClick={() => setDialog("action")}>
                    <Plus className="h-4 w-4 mr-1" /> Add action
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Incident record</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><dt className="text-xs text-slate-500">Date / time</dt><dd>{inc.incident_date} {inc.incident_time || ""}</dd></div>
            <div><dt className="text-xs text-slate-500">Location</dt><dd>{inc.location || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Category</dt><dd>{INCIDENT_CATEGORY_LABELS[inc.category] || inc.category || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Direction</dt><dd>{inc.direction === "outbound" ? "Home → School" : inc.direction === "return" ? "School → Home" : inc.direction || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Everyone safe</dt><dd>{inc.everyone_safe === null ? "—" : yn(inc.everyone_safe)}</dd></div>
            <div><dt className="text-xs text-slate-500">Injury / emergency services</dt><dd>{yn(inc.injury)} / {yn(inc.emergency_services)}</dd></div>
            <div><dt className="text-xs text-slate-500">Parent / school contacted</dt><dd>{yn(inc.parent_contacted)} / {yn(inc.school_contacted)}</dd></div>
            <div><dt className="text-xs text-slate-500">LA notification</dt><dd>{yn(inc.la_notification)}</dd></div>
            {inc.programme_ref && <div><dt className="text-xs text-slate-500">Programme</dt><dd>{inc.programme_ref}</dd></div>}
            {inc.session_ref && <div><dt className="text-xs text-slate-500">Session</dt><dd>{inc.session_ref}</dd></div>}
            {inc.journey_plan_ref && <div><dt className="text-xs text-slate-500">Route plan</dt><dd>{inc.journey_plan_ref}</dd></div>}
          </dl>
          <div className="mt-3 space-y-2 text-sm">
            <p><b>What happened:</b> {inc.description || "—"}</p>
            {inc.what_could_have_happened && <p><b>What could have happened:</b> {inc.what_could_have_happened}</p>}
            {inc.contributing_factors && <p><b>Contributing factors:</b> {inc.contributing_factors}</p>}
            {inc.recommended_prevention && <p><b>Recommended prevention:</b> {inc.recommended_prevention}</p>}
            <p><b>Immediate action:</b> {inc.immediate_action || "—"}</p>
            {inc.immediate_outcome && <p><b>Immediate outcome:</b> {inc.immediate_outcome}</p>}
            {inc.people_involved && <p><b>People involved:</b> {inc.people_involved}</p>}
            {inc.witnesses && <p><b>Witnesses:</b> {inc.witnesses}</p>}
          </div>
          {(inc.risk_review_required || inc.journey_plan_review_required || inc.training_plan_review_required) && (
            <p className="mt-2 text-xs text-amber-800" data-testid="incident-review-flags">
              Review flags: {[inc.risk_review_required && "Risk assessment", inc.journey_plan_review_required && "Route plan", inc.training_plan_review_required && "Training plan"].filter(Boolean).join(" · ")}
            </p>
          )}
          {inc.training_decision && (
            <p className="mt-2 text-sm" data-testid="incident-training-decision">
              <b>Training decision:</b> {inc.training_decision.decision.replace(/_/g, " ")} by {inc.training_decision.by_name}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Follow-up actions ({inc.actions?.length ?? 0})</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="incident-actions">
          {!inc.actions?.length && <p className="text-sm text-slate-600">No follow-up actions recorded.</p>}
          {inc.actions?.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3 text-sm" data-testid={`incident-action-${a.id}`}>
              <span className="flex-1">{a.action}</span>
              <span className="text-xs text-slate-500">{a.assigned_to_name || "Unassigned"} · due {a.due_date || "—"}</span>
              <Badge className={ACTION_STATUS_COLORS[a.status]}>{ACTION_STATUS_LABELS[a.status]}</Badge>
              {a.status !== "completed" && a.status !== "cancelled" && (
                <Select onValueChange={(v) => act(() => api.patch(`/incidents/${inc.id}/actions/${a.id}`, { status: v }), "Action updated")}>
                  <SelectTrigger data-testid={`incident-action-status-${a.id}`} className="h-10 w-40"><SelectValue placeholder="Update" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(ACTION_STATUS_LABELS).filter(([k]) => k !== "overdue").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Evidence</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(inc.evidence || []).map((e, i) => (
            <p key={i} className="text-xs text-slate-600" data-testid={`incident-evidence-${i}`}>
              {EVIDENCE_TYPES.find((t) => t.value === e.evidence_type)?.label || e.evidence_type}: {e.description || "—"} — {e.uploaded_by_name}
            </p>
          ))}
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={evType} onValueChange={setEvType}>
              <SelectTrigger data-testid="incident-evidence-type" className="h-11 w-44"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{EVIDENCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="h-11 flex-1 min-w-[160px]" placeholder="Description" data-testid="incident-evidence-desc" value={evPurpose} onChange={(e) => setEvPurpose(e.target.value)} />
            <Button variant="outline" className="h-11" data-testid="incident-evidence-upload" onClick={() => fileRef.current?.click()}>
              <Camera className="h-4 w-4 mr-1" /> Attach evidence
            </Button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { uploadEvidence(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </CardContent>
      </Card>

      {inc.reviews?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Management reviews</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {inc.reviews.map((r, i) => (
              <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`incident-review-${i}`}>
                <p className="font-medium">{r.reviewer_name} · {(r.at || "").slice(0, 10)}</p>
                <p className="text-slate-700">{r.findings}</p>
                {r.comments && <p className="text-xs text-slate-500">{r.comments}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {inc.amendments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Amendment history</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {inc.amendments.map((a, i) => (
              <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`incident-amendment-${i}`}>
                <p className="font-medium">{a.reason}</p>
                {a.note && <p className="text-slate-600">{a.note}</p>}
                <p className="text-xs text-slate-500">{(a.at || "").slice(0, 16).replace("T", " ")} by {a.author_name}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialog === "amend"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add amendment</DialogTitle><DialogDescription>The original submitted report is never overwritten.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label><Input data-testid="incident-amend-reason" value={amendForm.reason} onChange={(e) => setAmendForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Amendment detail</Label><Textarea data-testid="incident-amend-note" rows={3} value={amendForm.note} onChange={(e) => setAmendForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <Button data-testid="incident-amend-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !amendForm.reason.trim()}
              onClick={() => act(() => api.post(`/incidents/${inc.id}/amend`, amendForm), "Amendment recorded")}>Record amendment</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "action"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add follow-up action</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Action *</Label><Input data-testid="action-text" value={actionForm.action} onChange={(e) => setActionForm((f) => ({ ...f, action: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Assigned to</Label>
              <Select value={actionForm.assigned_to} onValueChange={(v) => setActionForm((f) => ({ ...f, assigned_to: v }))}>
                <SelectTrigger data-testid="action-assignee" className="h-11"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent className="bg-white">{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Due date</Label><Input data-testid="action-due" type="date" value={actionForm.due_date} onChange={(e) => setActionForm((f) => ({ ...f, due_date: e.target.value }))} /></div>
            <Button data-testid="action-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !actionForm.action.trim()}
              onClick={() => act(() => api.post(`/incidents/${inc.id}/actions`, actionForm), "Action added")}>Add action</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "review"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manager review</DialogTitle><DialogDescription>Severity reclassification and closure decisions are audit-logged.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Findings *</Label><Textarea data-testid="review-findings" rows={3} value={reviewForm.findings} onChange={(e) => setReviewForm((f) => ({ ...f, findings: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Further action</Label><Textarea data-testid="review-further" rows={2} value={reviewForm.further_action} onChange={(e) => setReviewForm((f) => ({ ...f, further_action: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Reclassify severity (optional)</Label>
              <Select value={reviewForm.new_severity} onValueChange={(v) => setReviewForm((f) => ({ ...f, new_severity: v === "keep" ? "" : v }))}>
                <SelectTrigger data-testid="review-severity" className="h-11"><SelectValue placeholder="Keep current" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="keep">Keep current</SelectItem>
                  {INCIDENT_SEVERITIES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[["risk_review", "Risk review"], ["journey_plan_review", "Route plan review"], ["training_plan_review", "Training plan review"], ["safeguarding_referral", "Safeguarding referral"], ["parent_notification", "Parent notified"], ["school_notification", "School notified"], ["la_notification", "LA notified"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`review-${k}`} checked={reviewForm[k]} onChange={(e) => setReviewForm((f) => ({ ...f, [k]: e.target.checked }))} className="h-4 w-4" />
                  {label}
                </label>
              ))}
            </div>
            <div className="space-y-1.5"><Label>Closure decision</Label>
              <Select value={reviewForm.closure_decision} onValueChange={(v) => setReviewForm((f) => ({ ...f, closure_decision: v }))}>
                <SelectTrigger data-testid="review-closure" className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="keep_open">Keep open / under review</SelectItem>
                  <SelectItem value="close">Close incident</SelectItem>
                  <SelectItem value="reopen">Reopen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Comments</Label><Textarea data-testid="review-comments" rows={2} value={reviewForm.comments} onChange={(e) => setReviewForm((f) => ({ ...f, comments: e.target.value }))} /></div>
            <Button data-testid="review-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !reviewForm.findings.trim()}
              onClick={() => act(() => api.post(`/incidents/${inc.id}/review`, reviewForm), "Review recorded")}>Record review</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "decision"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Training decision</DialogTitle><DialogDescription>A human manager decides whether training continues — the system never decides automatically.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Select value={decisionForm.decision} onValueChange={(v) => setDecisionForm((f) => ({ ...f, decision: v }))}>
              <SelectTrigger data-testid="decision-select" className="h-11"><SelectValue placeholder="Select decision" /></SelectTrigger>
              <SelectContent className="bg-white">{TRAINING_DECISIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="decision-comments" rows={2} placeholder="Comments" value={decisionForm.comments} onChange={(e) => setDecisionForm((f) => ({ ...f, comments: e.target.value }))} />
            <Button data-testid="decision-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !decisionForm.decision}
              onClick={() => act(() => api.post(`/incidents/${inc.id}/training-decision`, decisionForm), "Decision recorded")}>Record decision</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
